/**
 * Second-Chance Consent Prompt
 *
 * A non-intrusive prompt shown after high-value user milestones (e.g., first CSV export).
 * Only appears if analytics was rejected or never set, with a 30-day cooldown after dismissal.
 */

import { X, BarChart3 } from 'lucide-react'

interface SecondChanceConsentPromptProps {
  /** Whether the prompt is visible */
  visible: boolean
  /** Handler for dismissing the prompt */
  onDismiss: () => void
  /** Handler for enabling analytics */
  onEnableAnalytics: () => void
  /** Context message about what triggered the prompt */
  contextMessage?: string
}

export default function SecondChanceConsentPrompt({
  visible,
  onDismiss,
  onEnableAnalytics,
  contextMessage = "Glad you're using Optioneer!",
}: SecondChanceConsentPromptProps) {
  if (!visible) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-slide-up max-w-sm">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl dark:shadow-black/40 border border-gray-200 dark:border-white/10 p-4">
        {/* Close button */}
        <button
          onClick={onDismiss}
          className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="flex items-start gap-3 pr-6">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-700 dark:text-slate-300 mb-1">{contextMessage}</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
              Would you like to enable anonymous performance analytics to help us improve this tool?
              No ads attached.
            </p>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={onEnableAnalytics}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-lg transition-colors shadow-sm"
              >
                Enable performance analytics
              </button>
              <button
                onClick={onDismiss}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
